/**
 * HR — unified HR module.
 * Merges four HR sub-modules into a single page with 4 tabs:
 *   • Enrolled Employees   → <Employees readOnly embedded />
 *   • Interview            → <Interview embedded />
 *   • Onboarding           → <Onboarding embedded />
 *   • Offboarding          → <Offboarding embedded />
 *
 * Each embedded sub-module hides its own PageHeader so this page owns
 * the single top banner. Deep-link via ?tab=interview / onboarding / offboarding.
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/GradientCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Users, UsersRound, Sparkles, UserMinus, Building2, TrendingUp, ShieldCheck } from "lucide-react";
import { tabAccessibleFor } from "../lib/modules";

import Employees from "./Employees";
import Interview from "./Interview";
import Onboarding from "./Onboarding";
import Offboarding from "./Offboarding";
import SalaryHike from "./SalaryHike";
import Probation from "./Probation";

const TABS = [
  { key: "enrolled",    label: "Enrolled Employees", icon: Users },
  { key: "interview",   label: "Interview",          icon: UsersRound },
  { key: "onboarding",  label: "Onboarding",         icon: Sparkles },
  { key: "probation",   label: "Probation",          icon: ShieldCheck },
  { key: "offboarding", label: "Offboarding",        icon: UserMinus },
  { key: "salary_hike", label: "Salary Hike",        icon: TrendingUp },
];

export default function HR({ initialTab = "enrolled" } = {}) {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isMaster = user?.role === "master_admin";

  const visibleTabs = useMemo(
    () => TABS.filter((t) => tabAccessibleFor(user, "hr", t.key)),
    [user],
  );

  const startTab = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    const q = sp.get("tab");
    if (q && visibleTabs.some((t) => t.key === q)) return q;
    return visibleTabs.some((t) => t.key === initialTab) ? initialTab : (visibleTabs[0]?.key || "enrolled");
  }, [location.search, initialTab, visibleTabs]);

  const [tab, setTab] = useState(startTab);
  useEffect(() => { setTab(startTab); }, [startTab]);
  const changeTab = (k) => {
    setTab(k);
    const sp = new URLSearchParams(location.search);
    sp.set("tab", k);
    navigate({ pathname: location.pathname, search: sp.toString() }, { replace: true });
  };

  return (
    <div className="max-w-[1500px] space-y-6" data-testid="hr-page">
      <PageHeader
        tone="indigo"
        icon={Building2}
        eyebrow="HR Workspace"
        title="HR"
        moduleKey="hr"
        subtitle="Enrolled employees, interviews, onboarding and offboarding — one unified control room."
        testid="hr-banner"
      />

      <Tabs value={tab} onValueChange={changeTab} className="w-full">
        <TabsList className="flex flex-wrap gap-1 bg-slate-100 dark:bg-slate-900/60 p-1 rounded-lg" data-testid="hr-tabs-list">
          {visibleTabs.map(({ key, label, icon: Icon }) => (
            <TabsTrigger
              key={key}
              value={key}
              className="flex items-center gap-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm"
              data-testid={`hr-tab-${key}`}
            >
              <Icon className="w-3.5 h-3.5" /> {label}
            </TabsTrigger>
          ))}
        </TabsList>

        {tabAccessibleFor(user, "hr", "enrolled") && (
          <TabsContent value="enrolled" className="mt-4">
            <Employees embedded readOnly={!isMaster} />
          </TabsContent>
        )}

        {tabAccessibleFor(user, "hr", "interview") && (
          <TabsContent value="interview" className="mt-4">
            <Interview embedded />
          </TabsContent>
        )}

        {tabAccessibleFor(user, "hr", "onboarding") && (
          <TabsContent value="onboarding" className="mt-4">
            <Onboarding embedded />
          </TabsContent>
        )}

        {tabAccessibleFor(user, "hr", "probation") && (
          <TabsContent value="probation" className="mt-4">
            <Probation />
          </TabsContent>
        )}

        {tabAccessibleFor(user, "hr", "offboarding") && (
          <TabsContent value="offboarding" className="mt-4">
            <Offboarding embedded />
          </TabsContent>
        )}

        {tabAccessibleFor(user, "hr", "salary_hike") && (
          <TabsContent value="salary_hike" className="mt-4">
            <SalaryHike embedded />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function NoAccess() {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/60 p-8 text-center text-[13px] text-slate-500">
      You do not have access to this HR sub-module.
    </div>
  );
}
